import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import { trackEvent } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { PublicHeader } from '@/components/layout/PublicHeader';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { z } from 'zod';
import heroLoginImage from '@/assets/hero-login.png';
import loginArrowIcon from '@/assets/login-arrow.svg';
const authSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters')
});
const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});
export default function Login() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSignupSuccess = searchParams.get('signup') === 'success';
  const prefillEmail = searchParams.get('email') || '';
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const {
    signIn,
    user,
    profile,
    isAdmin
  } = useAuth();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();

  // Show success toast and focus password field after signup
  useEffect(() => {
    if (isSignupSuccess && prefillEmail) {
      toast({
        title: 'Account created successfully!',
        description: 'Enter your password to complete login.'
      });
      // Focus password field after a short delay
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [isSignupSuccess, prefillEmail, toast]);

  // Redirect logged-in users based on subscription status
  useEffect(() => {
    if (user && profile) {
      const paidPlans = ['admin', 'mastery', 'student'];
      const hasActiveSubscription = isAdmin || profile.membership_plan === 'admin' || profile.membership_status === 'active' && paidPlans.includes(profile.membership_plan);
      if (hasActiveSubscription) {
        navigate('/dashboard');
      } else {
        navigate('/subscription-required');
      }
    }
  }, [user, profile, isAdmin, navigate]);
  const validateForm = () => {
    try {
      authSchema.parse({
        email,
        password
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: {
          email?: string;
          password?: string;
        } = {};
        err.errors.forEach(error => {
          if (error.path[0] === 'email') fieldErrors.email = error.message;
          if (error.path[0] === 'password') fieldErrors.password = error.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };
  const validateEmail = () => {
    try {
      emailSchema.parse({
        email
      });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors({
          email: err.errors[0]?.message
        });
      }
      return false;
    }
  };
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    const {
      error
    } = await signIn(email, password);
    setIsLoading(false);
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Sign in failed',
        description: error.message === 'Invalid login credentials' ? 'Invalid email or password. Please try again.' : error.message
      });
    } else {
      // Clear signup params from URL
      if (isSignupSuccess) {
        setSearchParams({});
      }
      toast({
        title: isSignupSuccess ? 'Welcome to Tenacity!' : 'Welcome back!',
        description: isSignupSuccess ? 'Your subscription is now active.' : 'You have successfully signed in.'
      });
      navigate('/dashboard');
    }
  };
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;
    setIsLoading(true);
    try {
      // Only call the edge function - it will generate the reset link and send branded email
      const {
        data,
        error
      } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'password_reset',
          email: email
        }
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({
        title: 'Check your email',
        description: 'We sent you a password reset link. Please check your inbox.'
      });
      trackEvent('password_reset_requested');
      setShowForgotPassword(false);
    } catch (err: any) {
      console.error('Failed to send password reset email:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err.message || 'Failed to send password reset email. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>Login - Tenacity Music</title>
        <meta name="description" content="Login to your Tenacity Music account to continue learning jazz improvisation." />
      </Helmet>
      <PublicHeader />
      
      <main className="flex-1 pt-20">
        {/* Hero Section with Form */}
        <section className="container mx-auto px-4 pt-2 md:pt-10 pb-0">
          {/* Desktop Layout with Image */}
          <div className="hidden md:flex flex-col lg:flex-row gap-4 lg:gap-4 items-stretch rounded-t-[140px] overflow-hidden animate-hero-card" style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 38.93%, rgba(0, 0, 0, 0.1) 161.95%)'
        }}>
            {/* Left - Hero Image */}
            <div className="w-full lg:w-2/5 relative overflow-hidden">
              <img src={heroLoginImage} alt="Jazz pianist" className="w-full h-full object-cover animate-hero-image" />
            </div>

            {/* Right - Form */}
            <div className="w-full lg:w-3/5 p-8 md:p-10 lg:p-12 flex flex-col justify-center animate-hero-content">
              {showForgotPassword ? <>
                  <h1 className="page-hero-title mb-2">
                    <span className="text-[#A855F7]">Reset</span>
                    <br />
                    <span className="text-white">Your</span>
                    <br />
                    <span className="text-white">Password</span>
                  </h1>
                  <p className="text-muted-foreground mb-8">Enter your email to receive a reset link.</p>

                  <form onSubmit={handleForgotPassword} className="space-y-6">
                    <div className="space-y-2">
                      <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className={`bg-transparent border-0 border-b border-white/30 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#CB5EFF] transition-colors ${email ? 'border-[#CB5EFF]' : ''} ${errors.email ? 'border-destructive' : ''}`} />
                      {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                    </div>

                    <Button type="submit" className="w-full h-12 rounded-lg text-button uppercase bg-gradient-to-r from-[#A855F7] via-[#EC4899] to-[#F59E0B] hover:opacity-90 transition-opacity" disabled={isLoading}>
                      {isLoading ? <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </> : <>
                          Send Reset Link
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>}
                    </Button>

                    <button type="button" onClick={() => setShowForgotPassword(false)} className="w-full text-center text-muted-foreground hover:text-white transition-colors">
                      Back to Login
                    </button>
                  </form>
                </> : <>
                  {isSignupSuccess && <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30 flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-green-500 font-medium">Account created successfully!</p>
                        <p className="text-green-400/80 text-sm">Enter your password to complete login.</p>
                      </div>
                    </div>}
                  <h1 className="page-hero-title mb-2">
                    <span className="text-[#A855F7]">{isSignupSuccess ? 'Almost' : 'Login'}</span>
                    <br />
                    <span className="text-white">{isSignupSuccess ? 'There!' : 'And Step'}</span>
                    <br />
                    <span className="text-white">{isSignupSuccess ? 'One More' : 'Back Into'}</span>
                    <br />
                    <span className="text-white">{isSignupSuccess ? 'Step' : 'The Groove'}</span>
                  </h1>
                  <p className="text-white mb-8">{isSignupSuccess ? 'Enter your password to access your account.' : 'Welcome back! Login to your account.'}</p>

                  <form onSubmit={handleSignIn} className="space-y-6">
                    <div className="space-y-2">
                      <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} disabled={isSignupSuccess && !!prefillEmail} className={`bg-transparent border-0 border-b border-white/30 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#CB5EFF] transition-colors ${email ? 'border-[#CB5EFF]' : ''} ${errors.email ? 'border-destructive' : ''} ${isSignupSuccess && prefillEmail ? 'opacity-70' : ''}`} />
                      {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                    </div>
                    
                    <div className="space-y-2">
                      <Input ref={passwordInputRef} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className={`bg-transparent border-0 border-b border-white/30 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#CB5EFF] transition-colors ${password ? 'border-[#CB5EFF]' : ''} ${errors.password ? 'border-destructive' : ''}`} />
                      {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                    </div>

                    <div className="text-right">
                      <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-white hover:text-white/80 transition-colors">
                        Forgot password?
                      </button>
                    </div>

                    <Button type="submit" className="w-full h-12 rounded-lg text-button uppercase bg-gradient-to-r from-[#A855F7] via-[#EC4899] to-[#F59E0B] hover:opacity-90 transition-opacity" disabled={isLoading}>
                      {isLoading ? <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </> : <>
                          Login to Your Account
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>}
                    </Button>
                  </form>
                </>}
            </div>
          </div>

          {/* Mobile Layout - No Image, Centered */}
          <div className="md:hidden flex flex-col items-center px-4 py-8">
            {showForgotPassword ? <>
                <h1 className="text-hero text-center mb-4">
                  <span className="text-[#A855F7]">RESET</span>{' '}
                  <span className="text-white">YOUR PASSWORD</span>
                </h1>
                <p className="text-muted-foreground mb-8 text-center">Enter your email to receive a reset link.</p>

                <form onSubmit={handleForgotPassword} className="space-y-6 w-full max-w-sm">
                  <div className="space-y-2">
                    <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className={`bg-transparent border-0 border-b border-white/30 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#CB5EFF] transition-colors ${email ? 'border-[#CB5EFF]' : ''} ${errors.email ? 'border-destructive' : ''}`} />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  <Button type="submit" className="w-full h-12 rounded-lg text-button uppercase bg-gradient-to-r from-[#A855F7] via-[#EC4899] to-[#F59E0B] hover:opacity-90 transition-opacity" disabled={isLoading}>
                    {isLoading ? <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </> : <>
                        Send Reset Link
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>}
                  </Button>

                  <button type="button" onClick={() => setShowForgotPassword(false)} className="w-full text-center text-muted-foreground hover:text-white transition-colors">
                    Back to Login
                  </button>
                </form>
              </> : <>
                {isSignupSuccess && <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30 flex items-start gap-3 w-full max-w-sm">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-green-500 font-medium">Account created!</p>
                      <p className="text-green-400/80 text-sm">Enter your password to continue.</p>
                    </div>
                  </div>}
                <h1 className="text-hero text-center mb-4">
                  <span className="text-[#A855F7]">{isSignupSuccess ? 'ALMOST THERE!' : 'LOGIN'}</span>{' '}
                  <span className="text-white">{isSignupSuccess ? 'ONE MORE STEP' : 'AND STEP BACK INTO THE GROOVE'}</span>
                </h1>
                <p className="text-white mb-8 text-center">{isSignupSuccess ? 'Enter your password to access your account.' : 'Welcome back! Login to your account.'}</p>

                <form onSubmit={handleSignIn} className="space-y-6 w-full max-w-sm">
                  <div className="space-y-2">
                    <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} disabled={isSignupSuccess && !!prefillEmail} className={`bg-transparent border-0 border-b border-white/30 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#CB5EFF] transition-colors ${email ? 'border-[#CB5EFF]' : ''} ${errors.email ? 'border-destructive' : ''} ${isSignupSuccess && prefillEmail ? 'opacity-70' : ''}`} />
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Input ref={passwordInputRef} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className={`bg-transparent border-0 border-b border-white/30 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#CB5EFF] transition-colors ${password ? 'border-[#CB5EFF]' : ''} ${errors.password ? 'border-destructive' : ''}`} />
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  </div>

                  <div className="text-center">
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-white hover:text-white/80 transition-colors">
                      Forgot password?
                    </button>
                  </div>

                  <Button type="submit" className="w-full h-12 rounded-lg text-button uppercase bg-gradient-to-r from-[#A855F7] via-[#EC4899] to-[#F59E0B] hover:opacity-90 transition-opacity" disabled={isLoading}>
                    {isLoading ? <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </> : <>
                        Login
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>}
                  </Button>
                </form>
              </>}
          </div>
        </section>

        {/* Not a member? Signup now */}
        <ScrollReveal>
          <section className="py-6 md:py-[60px] text-center">
            <div className="container max-w-5xl mx-auto px-6 md:px-8">
              <h3 className="text-headline-3 font-display">
                Not a member?{' '}
                <Link to="/signup" className="text-[#FFFF00] inline-flex items-center gap-2 group">
                  Signup now
                  <img src={loginArrowIcon} alt="" className="w-10 h-10 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </h3>
            </div>
          </section>
        </ScrollReveal>

        {/* Trouble Logging In Section */}
        <ScrollReveal delay={100}>
          <section className="relative pb-3 md:pb-8">
            <div className="container mx-auto px-4">
              <div className="relative rounded-t-[70px] md:rounded-t-[140px] py-10 md:py-16" style={{
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
            }}>
                <div className="max-w-5xl mx-auto px-6 md:px-8 text-center">
                  <h2 className="text-headline-2 font-display mb-6">
                    <span className="text-white">Trouble Logging In?</span>
                    <br />
                    <span className="text-[#EF4444]">Connect With Our Team</span>
                  </h2>
                  <Button variant="outline" className="px-12 py-6 text-button uppercase border-white hover:bg-white/10 hover:border-white/50 transition-all" asChild>
                    <Link to="/contact">
                      Get Help
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Quote Section */}
        <ScrollReveal delay={150}>
          <section className="py-4 md:py-6 text-center">
            <div className="container max-w-5xl mx-auto px-6 md:px-8">
              <blockquote>
                <p className="quote">
                  "You’ve got to learn your <span className="text-[#EF4444]">instrument</span>. Then, you practice.
                </p>
              </blockquote>
              <p className="quote-attribution mb-16">— Charlie Parker</p>
            </div>
          </section>
        </ScrollReveal>
      </main>
      
      <PublicFooter />
    </div>;
}